import { Avatar, Badge, Box, Button, Divider, Drawer, Flex, Grid, Group, Input, SegmentedControl, Select, Text, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconInfoSquare } from "@tabler/icons";
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { RequestApi } from "../../apis";
import noimage from "../../assets/no-image.png";
import { IAuth } from "../../interfaces/IAuth";
import { IFundingRequest } from "../../interfaces/IFundingRequest";
import { IGeneral } from "../../interfaces/IGeneral";
import { IPermissions } from "../../interfaces/IPermissions";
import { IReference } from "../../models/General";
import { ScfFinanceRequestDetail } from "../../pages/scf/finance-request/detail-tabs";
import { tugrug } from "../../utils";
import { dateSecFormat } from "../../utils/date";
import { renderName } from "../../utils/render-name";
import { ColumnType, ITableRef, Table } from "../table";
import RenderText from "../ui/render-text";

const data = [
  {
    name: "Бүгд",
    code: "",
    color: "yellow",
  },
  {
    name: "Buyer",
    code: "BUYER",
    color: "blue",
  },
  {
    name: "Supplier",
    code: "SUPPLIER",
    color: "green",
  },
];

export const ScfFundingRequestList = () => {
  const ref = useRef<ITableRef>(null);
  const [action, setAction] = React.useState<string[]>([]);
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);
  const { currencies, fundingInstructionStatus, requestStatus } = useSelector((state: { general: IGeneral }) => state.general);
  const [query, setQuery] = React.useState("");
  const [debouncedQuery] = useDebouncedValue(query, 600);
  const [status, setStatus] = React.useState("");
  const [debouncedStatus] = useDebouncedValue(status, 600);
  const [business, setBusiness] = React.useState("");
  const [debouncedBusiness] = useDebouncedValue(business, 600);

  const columns = useHeader({
    user,
    currencies,
    requestStatus,
    fundingInstructionStatus,
    tugrug,
    onClick: (key, record) => {
      switch (key) {
        case "detail": {
          setAction(["detail", record.id, "SUPPLIER"]);
          break;
        }
      }
    },
  });

  const onCancel = async () => {
    setAction([]);
  };

  const reload = async () => {
    setTimeout(() => {
      ref.current?.reload();
    }, 200);
  };

  return (
    <>
      <Grid>
        <Grid.Col span={3}>
          <Input
            value={query}
            iconWidth={150}
            placeholder="хайх"
            maxLength={45}
            style={{ borderRight: "none", borderRadius: "4px 0 0 4px" }}
            icon={
              <Group w={"calc(100% - 20px)"} align="center" position="apart" noWrap>
                <Text size="sm" weight={450} c="#44566C">
                  {"Хайлт хийх"}
                </Text>
                <Text>:</Text>
              </Group>
            }
            onChange={(e) => {
              setQuery(e.currentTarget.value);
            }}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <SegmentedControl
            size="xs"
            onChange={(e) => setBusiness(e)}
            value={business}
            data={(data || []).map((item: IReference, index: number) => {
              return {
                value: item.code || "",
                label: (
                  <Badge variant="dot" color={item.color} style={{ border: "none" }} key={`${index}`}>
                    {item.name}
                  </Badge>
                ),
              };
            })}
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Select
            label=""
            value={status}
            data={[{ name: "Бүгд", code: "" }, ...(requestStatus || [])].map((c: IReference) => ({
              value: c.code,
              label: c.name,
            }))}
            iconWidth={150}
            placeholder="сонгох "
            icon={
              <Group w={"calc(100% - 20px)"} align="center" position="apart" noWrap>
                <Text size="sm" weight={450} c="#44566C">
                  {"Хүсэлт төлөв"}
                </Text>
                <Text>:</Text>
              </Group>
            }
            onChange={(e) => {
              setStatus(e as string);
            }}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <Divider mb={5} />
        </Grid.Col>
      </Grid>
      <Table
        ref={ref}
        name="scf.funding.request.supplier.list"
        columns={columns}
        filters={{ query: debouncedQuery, type: debouncedBusiness, status: debouncedStatus }}
        loadData={(data) => RequestApi.list(data!)}
      />
      <Drawer
        opened={action[0] === "detail"}
        onClose={() => {
          setAction([]);
        }}
        position="right"
        withCloseButton={false}
        styles={{ drawer: { overflowY: "scroll" } }}
        size="80%">
        <ScfFinanceRequestDetail action={action} onCancel={onCancel} setAction={setAction} reload={reload} />
      </Drawer>
    </>
  );
};

const useHeader = ({
  user,
  onClick,
  currencies,
  requestStatus,
  fundingInstructionStatus,
  tugrug,
}: {
  user: any;
  onClick: (key: string, record: IFundingRequest) => void;
  currencies: IReference[];
  requestStatus: IReference[];
  fundingInstructionStatus: any;
  tugrug: any;
}): ColumnType<IFundingRequest>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Үйлдэл",
    render: (record) => {
      return (
        <Flex gap="xs">
          {(user?.permissions || []).filter((c: IPermissions) => c.module === "SCF" && c.code === "SCF_REQUEST_MANAGEMENT_BL" && c.isView).length > 0 ? (
            <Tooltip label="Дэлгэрэнгүй" position="bottom">
              <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("detail", record)}>
                <IconInfoSquare />
              </Button>
            </Tooltip>
          ) : (
            <Tooltip label="Дэлгэрэнгүй" position="bottom">
              <Button variant="light" radius={100} w={35} h={35} p={0} onClick={() => onClick("", record)} disabled>
                <IconInfoSquare />
              </Button>
            </Tooltip>
          )}
        </Flex>
      );
    },
  },
  {
    title: "Хүсэлтийн #",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record.refCode || "-"} />
          <RenderText text={dateSecFormat(record.createdAt)} color="#3e166e" />
        </Box>
      );
    },
  },
  {
    title: "Хүсэлт илгээсэн бизнес",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.requestedBusiness?.profileName || "-"} />
          <RenderText text={record?.requestedBusiness?.refCode || "-"} color="#3e166e" />
        </Box>
      );
    },
  },
  {
    title: "Партнер",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.requestedBusiness?.partner?.businessName || "-"} />
          <RenderText text={record?.requestedBusiness?.partner?.refCode || "-"} color="#3e166e" />
        </Box>
      );
    },
  },
  {
    title: "Хөтөлбөр нэр",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.program?.name || "-"} />
          <RenderText text={record?.program?.refCode || "-"} color="#3e166e" />
        </Box>
      );
    },
  },
  {
    title: "Бүтээгдэхүүн",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.product?.name || "-"} />
          <RenderText text={record?.product?.refCode || "-"} color="#3e166e" />
        </Box>
      );
    },
  },
  {
    title: "Хүсэлтийн дүн",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={tugrug(record.requestedAmount || "0")} isAmount />;
    },
  },
  {
    title: "Валют",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Badge size="md" radius="sm" variant="outline">
          {currencies.find((c: any) => c.code === record.currency)?.name || "-"}
        </Badge>
      );
    },
  },
  {
    title: "Хүсэлт төлөв",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Badge
          size="md"
          radius="sm"
          variant="outline"
          style={{
            border: `${(requestStatus || []).find((c: any) => c?.code === record.requestStatus)?.color} 1px solid`,
            color: `${(requestStatus || []).find((c: any) => c?.code === record.requestStatus)?.color}`,
          }}>
          {(requestStatus || []).find((c: IReference) => c.code === record.requestStatus)?.name ?? "-"}
        </Badge>
      );
    },
  },
  {
    title: "Хоног",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={record.financePeriod || "0"} />;
    },
  },
  {
    title: "Санхүүжилт шимтгэл",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={tugrug(record.calculatedFeeAmount || "0")} />;
    },
  },
  {
    title: "Банк шимтгэл",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={tugrug(record.bankFeeAmount || "0")} />;
    },
  },
  {
    title: "Нэхэмжлэх",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={record.invRefCode || "-"} />;
    },
  },
  {
    title: "Нэхэмжлэх үлдэгдэл",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={tugrug(record.invAmountToPay || "0")} />;
    },
  },
  {
    title: "Нэхэмжлэх огноо",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={dateSecFormat(record.invCreatedAt)} />;
    },
  },
  {
    title: "Баталсан огноо",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={dateSecFormat(record.invConfirmedDate)} />;
    },
  },
  {
    title: "Нэхэмжлэх баталсан",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Flex align="center" gap="xs">
          <Avatar src={record?.invConfirmedUser?.avatar !== null ? record?.invConfirmedUser?.avatar : noimage} />
          <Box>
            <RenderText text={"Buyer user"} />
            <RenderText text={renderName(record?.invConfirmedUser)} />
          </Box>
        </Flex>
      );
    },
  },
  {
    title: "Харилцсан бизнес",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record.type === "SUPPLIER" ? record?.invReceiverBusiness?.profileName : record?.requestedBusiness?.profileName} />
          <RenderText text={record.type === "SUPPLIER" ? record?.invReceiverBusiness?.refCode : record?.requestedBusiness?.refCode} color="#3e166e" />
        </Box>
      );
    },
  },
  {
    title: "Харилцсан партнер",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Box>
          <RenderText
            text={record.type === "SUPPLIER" ? record?.invReceiverBusiness?.partner?.businessName : record?.requestedBusiness?.partner?.businessName}
          />
          <RenderText text={record.type === "SUPPLIER" ? record?.invReceiverBusiness?.partner?.refCode : record?.requestedBusiness?.partner?.refCode} />
        </Box>
      );
    },
  },
  {
    title: "Хүсэлт илгээсэн",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Flex align="center" gap="xs">
          <Avatar src={record?.requestedUser?.avatar !== null ? record?.requestedUser?.avatar : noimage} />
          <Box>
            <RenderText text={"Supplier user"} />
            <RenderText text={renderName(record?.requestedUser)} />
          </Box>
        </Flex>
      );
    },
  },
  {
    title: "Funding Instruction",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <>
          {record.fundingInstructionStatus !== null ? (
            <Badge size="md" radius="sm" variant="outline">
              {fundingInstructionStatus.find((item: IReference) => item.code === record.fundingInstructionStatus)?.name || "-"}
            </Badge>
          ) : (
            "-"
          )}
        </>
      );
    },
  },
  {
    title: "Банк ажилтан",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return (
        <Flex align="center" gap="xs">
          {record.respondedUser !== null ? (
            <Avatar src={record?.respondedUser?.avatar !== null ? record?.respondedUser?.avatar : noimage} />
          ) : (
            <Avatar src={noimage} />
          )}
          <RenderText text={renderName(record?.respondedUser)} />
        </Flex>
      );
    },
  },
  {
    title: "Олгох дүн",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={tugrug(record.approvedAmount || "0")} isAmount />;
    },
  },
  {
    title: "Шийдвэр огноо",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={dateSecFormat(record?.respondedDate)} />;
    },
  },
  {
    title: "Олгох огноо",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={dateSecFormat(record?.toDisburseDate)} />;
    },
  },
  {
    title: "Эргэн төлөх дүн",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={tugrug(record.repaymentAmount || "0")} isAmount />;
    },
  },
  {
    title: "Эргэн төлөх огноо",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={dateSecFormat(record?.repaymentDate)} />;
    },
  },
  {
    title: "Эргэн төлөх нэхэмжлэх",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={record?.repaymentInv?.refCode || "-"} />;
    },
  },
  {
    title: "Олгосон",
    sorter: true,
    dataIndex: "sku",
    render: (record) => {
      return <RenderText text={dateSecFormat(record?.toDisburseDate)} />;
    },
  },
];
